import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

const manualColumn = (name) => ({
  name,
  type: "TEXT",
  notNull: false,
  defaultValue: null,
  primaryKeyOrder: 0,
});

export default function AddTableDialog({
  open,
  onClose,
  dbName,
  tableOptions = [],
  getColumns,
  createTable,
  onCreated,
}) {
  const [templateTable, setTemplateTable] = useState("");
  const [newTableName, setNewTableName] = useState("");
  const [columns, setColumns] = useState([]);
  const [columnInput, setColumnInput] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setTemplateTable("");
      setNewTableName("");
      setColumns([]);
      setColumnInput("");
      setLoadingTemplate(false);
      setCreating(false);
      setError("");
    }
  }, [open]);

  const existingTableNames = useMemo(
    () => new Set(tableOptions.map((name) => String(name).trim().toLowerCase())),
    [tableOptions]
  );

  const handleTemplateChange = async (event) => {
    const tableName = event.target.value;
    setTemplateTable(tableName);
    setError("");

    if (!tableName) {
      setColumns([]);
      return;
    }

    try {
      setLoadingTemplate(true);
      const schema = (await getColumns?.(dbName, tableName)) ?? [];
      setColumns(schema);
    } catch (err) {
      console.error("Failed to load template columns:", err);
      setColumns([]);
      setError("Could not load the selected table schema.");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const addColumn = () => {
    const name = columnInput.trim();
    if (!name) return;

    const duplicate = columns.some(
      (column) => column.name.toLowerCase() === name.toLowerCase()
    );
    if (duplicate) {
      setError(`Column "${name}" already exists.`);
      return;
    }

    setColumns((current) => [...current, manualColumn(name)]);
    setColumnInput("");
    setError("");
  };

  const removeColumn = (name) => {
    setColumns((current) => current.filter((column) => column.name !== name));
    setError("");
  };

  const handleCreate = async () => {
    const tableName = newTableName.trim();

    if (!tableName) {
      setError("Enter a new table name.");
      return;
    }
    if (existingTableNames.has(tableName.toLowerCase())) {
      setError(`Table "${tableName}" already exists in this database.`);
      return;
    }
    if (!columns.length) {
      setError("Choose a template or add at least one column.");
      return;
    }

    try {
      setCreating(true);
      setError("");
      await createTable(dbName, tableName, columns);
      await onCreated?.(tableName);
      onClose();
    } catch (err) {
      console.error("Failed to create table:", err);
      const message =
        err?.response?.data?.message || err?.message || "Failed to create table.";
      setError(message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600 }}>Add table from template</DialogTitle>

      <DialogContent sx={{ pt: "14px !important" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <FormControl fullWidth size="small">
            <Select
              value={templateTable}
              onChange={handleTemplateChange}
              displayEmpty
              disabled={creating || loadingTemplate}
              renderValue={(value) => value || "Copy from table"}
              endAdornment={
                loadingTemplate ? (
                  <CircularProgress size={18} sx={{ mr: 3 }} />
                ) : null
              }
            >
              <MenuItem value="">
                <em>No template</em>
              </MenuItem>
              {tableOptions.map((tableName) => (
                <MenuItem key={tableName} value={tableName}>
                  {tableName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            fullWidth
            value={newTableName}
            onChange={(event) => setNewTableName(event.target.value)}
            placeholder="New table name"
            disabled={creating}
          />

          <Box>
            <Typography
              variant="overline"
              sx={{ display: "block", fontWeight: 700, letterSpacing: 1, lineHeight: 1.6 }}
            >
              Columns
            </Typography>

            {!columns.length && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Choose a template above, or add columns below.
              </Typography>
            )}

            {!!columns.length && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.75, mb: 2 }}>
                {columns.map((column) => (
                  <Chip
                    key={column.name}
                    label={column.name}
                    onDelete={() => removeColumn(column.name)}
                    size="small"
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
              <TextField
                size="small"
                fullWidth
                value={columnInput}
                onChange={(event) => setColumnInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addColumn();
                  }
                }}
                placeholder="Add column"
                disabled={creating}
              />
              <IconButton
                aria-label="Add column"
                onClick={addColumn}
                disabled={creating || !columnInput.trim()}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="secondary" onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={handleCreate}
          disabled={creating || loadingTemplate}
        >
          {creating ? <CircularProgress size={20} color="inherit" /> : "Create table"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
